import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Paper,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

import { getPreChartDocuments, getPreChartDocument } from "apiHandler/actions";

const DocumentList = ({ token, row, userData }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocIndex, setSelectedDocIndex] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [loading, setLoading] = useState(true);

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  useEffect(() => {
    getDocuments();
  }, []);

  const getDocuments = async () => {
    setLoading(true);
    try {
      const payload = {
        patient_id: `${row?.patient_id}`,
        encounter_id: `${row.encounter_id || row.id}`,
        practice_id: userData.selected_practice,
      };
      const res = await getPreChartDocuments(payload, token);
      setDocuments(res || []);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentFile = async (doc) => {
    setLoadingDoc(true);
    try {
      const payload = {
        type: "Documents",
        document_id: `${doc.id}`,
        practice_id: userData.selected_practice,
      };

      const blob = await getPreChartDocument(payload, token);
      const mimeType = blob.type || "application/octet-stream";
      const url = URL.createObjectURL(blob);

      setDocumentUrl({ url, mimeType });
    } catch (error) {
      console.error("Error loading document:", error);
      setDocumentUrl(null);
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleSelectDocument = (idx, doc) => {
    setSelectedDocIndex(idx);
    setDocumentUrl(null);
    getDocumentFile(doc);
  };

  const selectedDoc =
    selectedDocIndex !== null ? documents[selectedDocIndex] : null;

  return (
    <Box sx={{ display: "flex", height: "60vh" }}>
      <Paper variant="outlined" sx={{ width: 320, mr: 2, overflowY: "auto" }}>
        <Typography fontWeight={600} px={2} py={2}>
          Documents
        </Typography>
        <Divider />
        {loading ? (
          <LinearProgress />
        ) : (
          <List>
            {documents.length > 0 ? (
              documents.map((doc, idx) => (
                <ListItem key={doc.id} disablePadding>
                  <ListItemButton
                    selected={selectedDocIndex === idx}
                    onClick={() => handleSelectDocument(idx, doc)}
                  >
                    <ListItemText
                      primary={doc.filetitle}
                      secondary={doc.filedescription}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" px={2} py={2}>
                No documents available.
              </Typography>
            )}
          </List>
        )}
      </Paper>

      {/* Right: Document Viewer */}
      <Paper sx={{ flex: 1, p: 2, position: "relative" }}>
        {loadingDoc && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {selectedDoc && !loadingDoc && documentUrl ? (
          <>
            <Typography fontWeight={500} mb={1}>
              {selectedDoc.filetitle}
            </Typography>
            <Typography color="text.secondary" mb={2}>
              {selectedDoc.filedescription || selectedDoc.notes}
            </Typography>

            <Box
              sx={{
                height: "70vh",
                overflow: "auto",
                borderRadius: 1,
                border: "1px solid #ccc",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {documentUrl?.mimeType?.includes("pdf") ? (
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                  <Viewer
                    fileUrl={documentUrl.url}
                    plugins={[defaultLayoutPluginInstance]}
                  />
                </Worker>
              ) : documentUrl?.mimeType?.startsWith("image/") ? (
                <img
                  src={documentUrl.url}
                  alt={selectedDoc.filetitle}
                  style={{ maxWidth: "100%", maxHeight: "100%" }}
                />
              ) : (
                <Typography color="text.secondary">
                  Unsupported file type: {documentUrl?.mimeType || "unknown"}
                </Typography>
              )}
            </Box>
          </>
        ) : (
          !loadingDoc && (
            <Typography variant="body1" color="text.secondary">
              Select a document to view.
            </Typography>
          )
        )}
      </Paper>
    </Box>
  );
};

export default DocumentList;
